"use client";

import { useEffect, useState, type ReactNode } from "react";
import {
  Bot,
  BriefcaseBusiness,
  Check,
  ChevronLeft,
  CircleAlert,
  ClipboardCopy,
  Download,
  History,
  LoaderCircle,
  Maximize2,
  Plus,
  ShieldCheck,
  Sparkles,
  X,
  Ban,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type {
  ActivityEvent,
  Attachment,
  Conversation,
  Message,
  ToolAction,
  VisualPreview,
  WorkTask,
  WorkspaceMode,
} from "./ai-workspace-types";

export function WorkspaceHistory({
  mode,
  conversations,
  activeConversationId,
  loading,
  onNew,
  onOpen,
  isAdmin,
  onDelete,
}: {
  mode: WorkspaceMode;
  conversations: Conversation[];
  activeConversationId: string | null;
  loading: boolean;
  onNew: () => void;
  onOpen: (conversation: Conversation) => void;
  isAdmin: boolean;
  onDelete: (conversation: Conversation) => void;
}) {
  return (
    <>
      <div className="p-3">
        <Button
          variant="outline"
          onClick={onNew}
          className="h-10 w-full justify-start rounded-xl border-0 bg-transparent text-foreground hover:bg-foreground/[0.05]"
        >
          <Plus className="size-4" />
          New {mode === "chat" ? "chat" : "task"}
        </Button>
      </div>
      <div className="flex items-center gap-2 px-4 pb-2 pt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground/35">
        <History className="size-3.5" />
        Recent
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        {loading ? (
          <div className="grid place-items-center py-8">
            <LoaderCircle className="size-4 animate-spin text-foreground/35" />
          </div>
        ) : conversations.length === 0 ? (
          <p className="px-3 py-6 text-xs leading-5 text-foreground/35">
            Your {mode === "chat" ? "conversations" : "tasks"} will appear here.
          </p>
        ) : (
          conversations.map((conversation) => (
            <div key={conversation.id} className="group mb-1 flex items-center gap-1">
            <button
              type="button"
              onClick={() => onOpen(conversation)}
              className={`flex min-w-0 flex-1 items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm transition ${
                activeConversationId === conversation.id
                  ? "bg-foreground/[0.08] text-foreground"
                  : "text-foreground/56 hover:bg-foreground/[0.05] hover:text-foreground"
              }`}
            >
              <ChevronLeft className="size-3.5 rotate-180 opacity-30" />
              <span className="truncate">{conversation.title}</span>
            </button>
            {isAdmin && <button type="button" onClick={() => onDelete(conversation)} aria-label={`Delete ${conversation.title}`} title="Delete chat history" className="grid size-9 shrink-0 place-items-center rounded-lg text-foreground/30 hover:bg-red-500/10 hover:text-red-500"><Trash2 className="size-4" /></button>}
            </div>
          ))
        )}
      </div>
    </>
  );
}

export function MessageBubble({
  message,
  closingBrowserSessionId,
  onCloseBrowserSession,
  onClearPreview,
  onDownloadAttachment,
}: {
  message: Message;
  closingBrowserSessionId: string | null;
  onCloseBrowserSession: (sessionId: string) => void;
  onClearPreview: (previewId: string) => void;
  onDownloadAttachment: (attachment: Attachment) => void;
}) {
  const isUser = message.role === "user";
  if (message.role !== "user" && message.role !== "assistant") return null;

  return (
    <article className={isUser ? "flex justify-end" : "flex justify-start"}>
      <div
        className={
          isUser
            ? "max-w-[88%] rounded-[22px] bg-foreground/[0.055] px-4 py-3 text-[15px] leading-7 text-foreground sm:max-w-[78%]"
            : "w-full text-[15px] leading-7 text-foreground/90"
        }
      >
        {!isUser && typeof message.thinking_seconds === "number" && (
          <ThinkingStatus
            mode="chat"
            completedSeconds={message.thinking_seconds}
          />
        )}
        {!isUser && (
          <div className={`${typeof message.thinking_seconds === "number" ? "mt-3" : ""} mb-2 flex items-center gap-2 text-xs font-semibold text-foreground/40`}>
            <span className="grid size-6 place-items-center rounded-full bg-foreground/[0.04]">
              <Bot className="size-3.5" />
            </span>
            RUNYI
          </div>
        )}
        {!isUser && message.visual_previews && message.visual_previews.length > 0 && (
          <EphemeralBrowserPreviews
            previews={message.visual_previews}
            closingBrowserSessionId={closingBrowserSessionId}
            onCloseBrowserSession={onCloseBrowserSession}
            onClearPreview={onClearPreview}
          />
        )}
        {!isUser && message.attachments && message.attachments.length > 0 && (
          <div className="mb-3 flex flex-col gap-2">
            {message.attachments.map((attachment) => (
              <a
                key={attachment.storage_path}
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  onDownloadAttachment(attachment);
                }}
                className="inline-flex w-fit max-w-full items-center gap-2 rounded-xl border border-foreground/10 bg-foreground/[0.035] px-3 py-2 text-sm font-medium text-foreground/75 underline-offset-4 hover:bg-foreground/[0.06] hover:underline"
              >
                <Download className="size-4 shrink-0" />
                <span className="truncate">Download {attachment.name}</span>
              </a>
            ))}
          </div>
        )}
        {isUser ? (
          <div>
            {message.attachments && message.attachments.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {message.attachments.map((attachment) => (
                  <span
                    key={attachment.storage_path}
                    className="inline-flex max-w-full items-center rounded-full border border-foreground/10 bg-background/40 px-2.5 py-1 text-[10px] text-foreground/62"
                  >
                    <span className="max-w-[190px] truncate">{attachment.name}</span>
                  </span>
                ))}
              </div>
            )}
            <div className="whitespace-pre-wrap break-words">{message.content}</div>
          </div>
        ) : (
          <MarkdownContent text={message.content} />
        )}
      </div>
    </article>
  );
}

function formatThinkingTime(totalSeconds: number) {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder > 0 ? `${minutes}m ${remainder}s` : `${minutes}m`;
}

export function LiveProcessStatus({
  mode,
  events,
  active,
  startedAt,
}: {
  mode: WorkspaceMode;
  events: ActivityEvent[];
  active: boolean;
  startedAt: number | null;
}) {
  const [open, setOpen] = useState(true);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!active || startedAt === null) return;
    const update = () => setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [active, startedAt]);

  useEffect(() => {
    if (active) setOpen(true);
    else if (events.length > 0) {
      const timer = window.setTimeout(() => setOpen(false), 1400);
      return () => window.clearTimeout(timer);
    }
  }, [active, events.length]);

  const current = [...events].reverse().find((event) => event.status === "active")
    ?? [...events].reverse().find((event) => event.status === "waiting_approval")
    ?? events[events.length - 1];
  const fallback = mode === "work" ? "Working through the task" : "Working on your response";
  const completed = events.filter((event) => event.status === "completed").length;

  return (
    <div className="w-full py-1 text-sm text-foreground/58" aria-live={active ? "polite" : "off"}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex min-h-9 items-center gap-2 rounded-xl px-1 py-1.5 text-left transition hover:text-foreground/75"
        aria-expanded={open}
      >
        {active ? (
          <span className="relative grid size-5 place-items-center" aria-hidden="true">
            <span className="absolute size-3 animate-ping rounded-full bg-primary/20" />
            <span className="relative size-1.5 rounded-full bg-primary" />
          </span>
        ) : (
          <Check className="size-4 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
        )}
        <span className="font-medium text-foreground/68">
          {active ? (current?.label ?? fallback) : "Process complete"}
        </span>
        {active && startedAt !== null && (
          <span className="tabular-nums text-[11px] text-foreground/32">{formatThinkingTime(elapsedSeconds)}</span>
        )}
        {!active && completed > 0 && (
          <span className="text-[11px] text-foreground/30">{completed} steps</span>
        )}
        <ChevronLeft
          className={`size-3.5 text-foreground/30 transition-transform ${open ? "-rotate-90" : "rotate-180"}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="ml-1 mt-1 space-y-2.5 pl-6 text-[12px] leading-5 text-foreground/45">
          {(events.length ? events : [{
            id: "local-process",
            request_id: "local",
            mode,
            sequence: 0,
            event_key: "starting",
            label: fallback,
            status: "active" as const,
            created_at: new Date().toISOString(),
          }]).map((event) => (
            <div key={event.id} className="flex items-start gap-2.5">
              <span className="mt-[7px] grid size-2 shrink-0 place-items-center">
                {event.status === "active" ? (
                  <span className="size-1.5 animate-pulse rounded-full bg-primary" />
                ) : event.status === "failed" ? (
                  <span className="size-1.5 rounded-full bg-red-400" />
                ) : event.status === "waiting_approval" ? (
                  <span className="size-1.5 rounded-full bg-amber-400" />
                ) : (
                  <span className="size-1.5 rounded-full bg-foreground/22" />
                )}
              </span>
              <div className="min-w-0">
                <p className={event.status === "active" ? "font-medium text-foreground/68" : "text-foreground/46"}>
                  {event.label}
                </p>
                {event.detail && <p className="mt-0.5 break-words text-[11px] text-foreground/32">{event.detail}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ThinkingStatus({
  mode,
  startedAt = null,
  completedSeconds,
  label,
}: {
  mode: WorkspaceMode;
  startedAt?: number | null;
  completedSeconds?: number;
  label?: string;
}) {
  const active = typeof startedAt === "number";
  const [open, setOpen] = useState(active);
  const [elapsedSeconds, setElapsedSeconds] = useState(() =>
    active ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : (completedSeconds ?? 0),
  );

  useEffect(() => {
    if (!active || startedAt === null) {
      setElapsedSeconds(completedSeconds ?? 0);
      return;
    }

    setOpen(true);
    const updateElapsed = () => {
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    };
    updateElapsed();
    const timer = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(timer);
  }, [active, completedSeconds, startedAt]);

  const activity = elapsedSeconds < 3
    ? "Understanding your request"
    : elapsedSeconds < 15
      ? mode === "work" ? "Working through the task" : "Developing the response"
      : "Preparing a complete response";

  return (
    <div className="w-full text-sm text-foreground/58" aria-live={active ? "polite" : "off"}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex min-h-9 items-center gap-2 rounded-lg px-1.5 py-1.5 text-left transition hover:bg-foreground/[0.04]"
        aria-expanded={open}
      >
        {active ? (
          <span className="relative grid size-5 place-items-center" aria-hidden="true">
            <span className="absolute size-4 animate-ping rounded-full bg-foreground/10" />
            <Sparkles className="relative size-4 animate-pulse text-foreground/55" />
          </span>
        ) : (
          <Sparkles className="size-4 text-foreground/48" aria-hidden="true" />
        )}
        <span className="font-medium text-foreground/65">
          {active ? (label || "Working…") : `Processed in ${formatThinkingTime(completedSeconds ?? elapsedSeconds)}`}
        </span>
        {active && (
          <span className="tabular-nums text-xs text-foreground/35">{formatThinkingTime(elapsedSeconds)}</span>
        )}
        <ChevronLeft
          className={`size-3.5 text-foreground/35 transition-transform ${open ? "-rotate-90" : "rotate-180"}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="ml-4 border-l border-foreground/10 py-1.5 pl-4 text-[13px] leading-5 text-foreground/45">
          <div className="flex items-center gap-2">
            {active && <span className="size-1.5 animate-pulse rounded-full bg-foreground/35" aria-hidden="true" />}
            <span>{active ? activity : "RUNYI completed the request and prepared the response below."}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function EphemeralBrowserPreviews({
  previews,
  closingBrowserSessionId,
  onCloseBrowserSession,
  onClearPreview,
}: {
  previews: VisualPreview[];
  closingBrowserSessionId: string | null;
  onCloseBrowserSession: (sessionId: string) => void;
  onClearPreview: (previewId: string) => void;
}) {
  const [selected, setSelected] = useState<VisualPreview | null>(null);

  return (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        {previews.map((preview) => (
          <button
            key={preview.id}
            type="button"
            onClick={() => setSelected(preview)}
            className="group overflow-hidden rounded-2xl border border-foreground/10 bg-foreground/[0.025] text-left shadow-sm"
          >
            <div className="relative aspect-[16/10] overflow-hidden bg-black/5">
              <img
                src={preview.image_data_url}
                alt={preview.title || "Remote browser screenshot"}
                className="h-full w-full object-contain transition-transform duration-200 group-hover:scale-[1.01]"
              />
              <span className="absolute right-2 top-2 grid size-8 place-items-center rounded-full bg-black/65 text-white backdrop-blur">
                <Maximize2 className="size-4" />
              </span>
            </div>
            <div className="px-3 py-2.5">
              <p className="truncate text-xs font-semibold text-foreground/78">
                {preview.title || "Browser screenshot"}
              </p>
              <p className="mt-0.5 truncate text-[10px] text-foreground/38">
                {preview.session_closed
                  ? "Session closed · temporary preview"
                  : "Temporary preview · not saved to conversation history"}
              </p>
              <div className="mt-2 flex gap-2">
                {preview.session_id && !preview.session_closed ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={closingBrowserSessionId === preview.session_id}
                    onClick={(event) => {
                      event.stopPropagation();
                      onCloseBrowserSession(preview.session_id as string);
                    }}
                    className="h-7 rounded-full border-foreground/12 px-2.5 text-[10px]"
                  >
                    {closingBrowserSessionId === preview.session_id ? (
                      <LoaderCircle className="size-3 animate-spin" />
                    ) : (
                      <X className="size-3" />
                    )}
                    Close & clear
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={(event) => {
                      event.stopPropagation();
                      onClearPreview(preview.id);
                    }}
                    className="h-7 rounded-full border-foreground/12 px-2.5 text-[10px]"
                  >
                    <X className="size-3" />
                    Dismiss
                  </Button>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-3 sm:p-6">
          <button
            type="button"
            aria-label="Close screenshot preview"
            onClick={() => setSelected(null)}
            className="absolute right-3 top-3 z-10 grid size-10 place-items-center rounded-full bg-white/12 text-white backdrop-blur hover:bg-white/20"
          >
            <X className="size-5" />
          </button>
          <div className="flex max-h-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-black shadow-2xl">
            <div className="min-h-0 flex-1 overflow-auto">
              <img
                src={selected.image_data_url}
                alt={selected.title || "Remote browser screenshot"}
                className="mx-auto block max-h-[82vh] max-w-full object-contain"
              />
            </div>
            <div className="border-t border-white/10 px-4 py-3 text-white">
              <p className="text-sm font-semibold">{selected.title || "Browser screenshot"}</p>
              {selected.url && (
                <p className="mt-1 break-all text-[11px] text-white/50">{selected.url}</p>
              )}
              <p className="mt-1 text-[10px] text-white/40">
                Ephemeral browser image · cleared from the workspace when the session is cleaned up or the page reloads
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function WorkTaskCard({
  task,
  busy,
  onRetry,
  onCancel,
}: {
  task: WorkTask;
  busy: boolean;
  onRetry: () => void;
  onCancel: () => void;
}) {
  const isActive = task.status === "queued" || task.status === "running";
  const statusLabel =
    task.status === "queued"
      ? "Queued"
      : task.status === "running"
        ? "Running in background"
        : task.status === "failed"
          ? "Needs attention"
          : task.status;

  return (
    <div className="rounded-2xl bg-foreground/[0.028] p-4">
      <div className="flex items-start gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-foreground/[0.04]">
          {isActive ? <LoaderCircle className="size-4 animate-spin" /> : <BriefcaseBusiness className="size-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold">{statusLabel}</p>
            <span className="text-[10px] text-foreground/35">
              Attempt {task.attempt_count}/{task.max_attempts}
            </span>
          </div>
          {isActive && (
            <div className="mt-2">
              <ThinkingStatus
                mode="work"
                startedAt={Date.parse(task.created_at)}
                label={task.status === "queued" ? "Waiting to start…" : "Thinking…"}
              />
            </div>
          )}
          <p className="mt-1 line-clamp-3 text-sm leading-6 text-foreground/58">{task.prompt}</p>
          {task.error && (
            <p className="mt-2 rounded-xl bg-red-500/[0.07] px-3 py-2 text-xs leading-5 text-red-600 dark:text-red-300">
              {task.error}
            </p>
          )}
          <div className="mt-3 flex gap-2">
            {task.status === "failed" && task.attempt_count < task.max_attempts && (
              <Button size="sm" variant="outline" disabled={busy} onClick={onRetry} className="rounded-full">
                {busy ? <LoaderCircle className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
                Resume
              </Button>
            )}
            {(task.status === "queued" || task.status === "running" || task.status === "failed") && (
              <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel} className="rounded-full">
                <Ban className="size-4" />
                Cancel
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProtectedActionCard({
  action,
  busy,
  onApprove,
  onReject,
}: {
  action: ToolAction;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  const label = action.tool_name === "m4_member_health_repair"
    ? "Repair verified member tenant-health evidence"
    : action.tool_name === "m4_member_session_recovery"
      ? "Recover the member tenant session"
      : action.tool_name === "github_merge_pull_request"
    ? "Merge pull request to production"
    : action.tool_name === "cloudflare_retry_deployment"
      ? "Retry Cloudflare deployment"
      : action.tool_name === "cloudflare_rollback_deployment"
        ? "Rollback Cloudflare deployment"
        : action.tool_name === "supabase_apply_migration"
          ? "Apply Supabase database migration"
          : action.tool_name === "browser_upload_file"
            ? "Approve file upload to website"
            : action.tool_name === "browser_sensitive_click"
              ? "Approve consequential browser click"
              : action.tool_name === "browser_sensitive_press"
                ? "Approve consequential browser submission"
                : action.tool_name;

  return (
    <div className="rounded-2xl bg-amber-300/[0.07] p-4">
      <div className="flex items-start gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-amber-300/10">
          <ShieldCheck className="size-4 text-amber-600 dark:text-amber-200" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Administrator approval required</p>
          <p className="mt-1 text-sm text-foreground/65">{label}</p>
          {typeof action.arguments.project_ref === "string" && (
            <p className="mt-1 break-all text-xs font-medium text-foreground/70">
              Target project: {action.arguments.project_ref}
            </p>
          )}
          <pre className="mt-3 max-h-40 overflow-auto rounded-xl bg-foreground/[0.05] p-3 text-[11px] leading-5 text-foreground/55">
            {JSON.stringify(action.arguments, null, 2)}
          </pre>
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              onClick={onApprove}
              disabled={busy}
              className="rounded-full"
            >
              {busy ? <LoaderCircle className="size-4 animate-spin" /> : <Check className="size-4" />}
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onReject}
              disabled={busy}
              className="rounded-full border-foreground/15"
            >
              <Ban className="size-4" />
              Reject
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InlineMarkdown({ text }: { text: string }) {
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|https?:\/\/[^\s<]+)/g;
  return (
    <>
      {text.split(pattern).filter(Boolean).map((part, index) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={index} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith("`") && part.endsWith("`")) {
          return <code key={index} className="rounded bg-foreground/[0.08] px-1.5 py-0.5 font-mono text-[0.92em]">{part.slice(1, -1)}</code>;
        }
        if (/^https?:\/\//.test(part)) {
          return (
            <a
              key={index}
              href={part}
              target="_blank"
              rel="noreferrer"
              className="break-all underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground"
            >
              {part}
            </a>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
}

async function copyToClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  if (!copied) throw new Error("Copy is not supported in this browser.");
}

function CopyBox({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await copyToClipboard(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-foreground/12 bg-foreground/[0.035]">
      <div className="flex items-center justify-between border-b border-foreground/8 px-3 py-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground/42">
          Copy box
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => void handleCopy()}
          className="h-8 rounded-full px-3 text-xs font-medium"
          aria-label="Copy box content"
        >
          {copied ? <Check className="size-3.5" /> : <ClipboardCopy className="size-3.5" />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <pre className="max-h-[65vh] overflow-auto whitespace-pre-wrap break-words p-4 font-mono text-[13px] leading-6 text-foreground/88">
        <code>{text}</code>
      </pre>
    </div>
  );
}

function MarkdownContent({ text }: { text: string }) {
  const blocks = text.split(/(```[\s\S]*?```)/g).filter(Boolean);

  return (
    <div className="space-y-3 break-words">
      {blocks.map((block, blockIndex) => {
        if (block.startsWith("```") && block.endsWith("```")) {
          const inner = block.slice(3, -3);
          const newline = inner.indexOf("\n");
          const language = newline >= 0 ? inner.slice(0, newline).trim() : "";
          const code = newline >= 0 ? inner.slice(newline + 1) : inner;
          const normalizedLanguage = language.toLowerCase();

          if (normalizedLanguage === "copy" || normalizedLanguage === "copybox" || normalizedLanguage === "copy-box") {
            return <CopyBox key={blockIndex} text={code.trimEnd()} />;
          }

          return (
            <div key={blockIndex} className="overflow-hidden rounded-xl border border-foreground/10 bg-foreground/[0.045]">
              {language && (
                <div className="border-b border-foreground/8 px-3 py-1.5 text-[10px] uppercase tracking-wider text-foreground/35">
                  {language}
                </div>
              )}
              <pre className="overflow-x-auto p-3 text-[12px] leading-5">
                <code>{code.trimEnd()}</code>
              </pre>
            </div>
          );
        }

        const lines = block.split("\n");
        const nodes: ReactNode[] = [];
        let index = 0;

        while (index < lines.length) {
          const line = lines[index];
          if (!line.trim()) {
            index += 1;
            continue;
          }

          const heading = line.match(/^(#{1,3})\s+(.+)$/);
          if (heading) {
            const level = heading[1].length;
            const cls = level === 1
              ? "text-xl font-semibold tracking-tight"
              : level === 2
                ? "text-lg font-semibold tracking-tight"
                : "text-base font-semibold";
            nodes.push(
              <div key={`h-${blockIndex}-${index}`} className={cls}>
                <InlineMarkdown text={heading[2]} />
              </div>,
            );
            index += 1;
            continue;
          }

          if (/^\s*[-*]\s+/.test(line)) {
            const items: string[] = [];
            while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
              items.push(lines[index].replace(/^\s*[-*]\s+/, ""));
              index += 1;
            }
            nodes.push(
              <ul key={`ul-${blockIndex}-${index}`} className="list-disc space-y-1 pl-5">
                {items.map((item, itemIndex) => (
                  <li key={itemIndex}><InlineMarkdown text={item} /></li>
                ))}
              </ul>,
            );
            continue;
          }

          if (/^\s*\d+\.\s+/.test(line)) {
            const items: string[] = [];
            while (index < lines.length && /^\s*\d+\.\s+/.test(lines[index])) {
              items.push(lines[index].replace(/^\s*\d+\.\s+/, ""));
              index += 1;
            }
            nodes.push(
              <ol key={`ol-${blockIndex}-${index}`} className="list-decimal space-y-1 pl-5">
                {items.map((item, itemIndex) => (
                  <li key={itemIndex}><InlineMarkdown text={item} /></li>
                ))}
              </ol>,
            );
            continue;
          }

          const paragraph: string[] = [line];
          index += 1;
          while (
            index < lines.length &&
            lines[index].trim() &&
            !/^(#{1,3})\s+/.test(lines[index]) &&
            !/^\s*[-*]\s+/.test(lines[index]) &&
            !/^\s*\d+\.\s+/.test(lines[index])
          ) {
            paragraph.push(lines[index]);
            index += 1;
          }
          nodes.push(
            <p key={`p-${blockIndex}-${index}`} className="leading-7">
              <InlineMarkdown text={paragraph.join(" ")} />
            </p>,
          );
        }

        return <div key={blockIndex} className="space-y-3">{nodes}</div>;
      })}
    </div>
  );
}

export function CenteredState({
  icon: Icon,
  title,
  body,
}: {
  icon: typeof CircleAlert;
  title: string;
  body: string;
}) {
  return (
    <div className="grid min-h-[55vh] place-items-center">
      <div className="max-w-md text-center">
        <div className="mx-auto grid size-12 place-items-center rounded-full border border-foreground/10 bg-foreground/[0.035]">
          <Icon className="size-5 text-foreground/55" />
        </div>
        <h2 className="mt-4 text-xl font-semibold">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-foreground/45">{body}</p>
      </div>
    </div>
  );
}
